import React, { useLayoutEffect, useState, useEffect } from "react"
import { motion, AnimatePresence, useCycle } from "framer-motion"
import { navigate } from "gatsby"
import MultiplayerLogo from "../../../src/assets/multiplayer.svg"
import AnnotationIcon from "../../../src/assets/annotation.svg"
import LightBulbIcon from "../../../src/assets/light-bulb.svg"
import BookIcon from "../../../src/assets/book-open.svg"
import Hamburger from "./Hamburger"
import DropdownNavItem from "./DropdownNavItem"
import { triggerPlausibleEvent } from "../../services/plausibleService"
import "./Header.scss"

const animations = {
  initial: {
    y: -590,
    height: 0,
    opacity: 0,
  },
  animate: {
    y: 0,
    height: 590,
    opacity: 1,
    transition: {
      ease: "easeOut",
      duration: 0.2,
    },
  },
  exit: {
    y: -590,
    height: 0,
    opacity: 0,
    transition: {
      ease: "easeIn",
      duration: 0.2,
    },
  },
}

const Header = () => {
  const isBrowser = typeof window !== "undefined"
  const [isOpen, toggleOpen] = useCycle(false, true)
  const [width, setWidth] = useState(0)
  const [isScrolled, setIsScrolled] = useState(false)

  const handleSize = () => {
    if (isBrowser) {
      setWidth(window.innerWidth)
    }
  }

  useLayoutEffect(() => {
    if (!isBrowser) {
      return
    }

    handleSize()
    window.addEventListener("resize", handleSize)

    return () => window.removeEventListener("resize", handleSize)
  }, [])

  useEffect(() => {
    if (!isBrowser) return

    const handleScroll = () => {
      const scrollPosition = window.scrollY
      setIsScrolled(scrollPosition > 50)
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  useEffect(() => {
    const vectorScript = document.createElement("script")
    const sourceScript = document.createElement("script")
    vectorScript.innerHTML = `
          !function(e,r){try{if(e.vector)return void console.log("Vector snippet included more than once.");var t={};t.q=t.q||[];for(var o=["load","identify","on"],n=function(e){return function(){var r=Array.prototype.slice.call(arguments);t.q.push([e,r])}},c=0;c<o.length;c++){var a=o[c];t[a]=n(a)}if(e.vector=t,!t.loaded){var i=r.createElement("script");i.type="text/javascript",i.async=!0,i.src="https://cdn.vector.co/pixel.js";var l=r.getElementsByTagName("script")[0];l.parentNode.insertBefore(i,l),t.loaded=!0}}catch(e){console.error("Error loading Vector:",e)}}(window,document);
    vector.load("07b585ba-f7f6-4e72-8eb1-c0810d9b7c0f");
    `
    sourceScript.innerHTML = `
      !function(e,s,t,n,c,i,o,r,u){e[c]||((o=e[c]=function(){o.process?o.process.apply(o,arguments):o.queue.push(arguments)}).queue=[],o.t=+new Date,(r=s.createElement(t)).async=1,r.src="https://pixel.source.app/versions/3/sourcepixel.min.js?t="+864e5*Math.ceil(new Date/864e5),(u=s.getElementsByTagName(t)[0]).parentNode.insertBefore(r,u))}(window,document,"script",0,"sourcePixel"),sourcePixel("init","d76906b3-dcdf-499b-95f9-80deb370bb9f"),sourcePixel("event","pageload")
    `
    document.head.appendChild(vectorScript)
    document.head.appendChild(sourceScript)

    return () => {
      document.head.removeChild(vectorScript)
      document.head.removeChild(sourceScript)
    }
  }, [])

  if (width && width <= 1020) {
    return (
      <header className="responsive-multiplayer-header">
        <div className="responsive-multiplayer-header-container">
          <MultiplayerLogo
            onClick={() => {
              triggerPlausibleEvent("HomeFromMenu")
              navigate("/")
            }}
          />
          <Hamburger isOpen={isOpen} toggle={toggleOpen} />
        </div>
        <AnimatePresence>
          {isOpen ? (
            <motion.div
              className="responsive-multiplayer-links-container"
              {...animations}
            >
              <a
                onClick={() => {
                  triggerPlausibleEvent("PricingFromMenu")
                  navigate("/pricing")
                }}
              >
                Pricing
              </a>
              <a onClick={() => navigate("/team")}>Team</a>
              <a
                onClick={() => {
                  triggerPlausibleEvent("LearnFromMenu")
                  navigate("/content-library")
                }}
              >
                Learn
              </a>
              <a
                onClick={() => triggerPlausibleEvent("BlogFromMenu")}
                href="https://www.multiplayer.app/blog"
                rel="noreferrer"
              >
                Blog
              </a>
              <a
                onClick={() => triggerPlausibleEvent("DiscordFromMenu")}
                href="https://discord.gg/q9K3mDzfrx"
                target="_blank"
                rel="noreferrer"
              >
                Discord
              </a>
              <a
                onClick={() => {
                  triggerPlausibleEvent("ContactFromMenu")
                  navigate("/contact")
                }}
              >
                Contact
              </a>
              <a
                onClick={() => {
                  navigate("/docs")
                }}
              >
                Docs
              </a>
              <a
                href="https://cal.com/multiplayer/30min"
                target="_blank"
                rel="noreferrer"
              >
                Book a Demo
              </a>
              <a
                onClick={() => triggerPlausibleEvent("SignupFromMenu")}
                rel="noreferrer"
                target="_blank"
                href="https://go.multiplayer.app"
              >
                Sign In
              </a>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </header>
    )
  }

  if (width && width > 1020) {
    return (
      <header
        className={`multiplayer-header ${isScrolled ? "is-scrolled" : ""}`}
      >
        <div className="backdrop"></div>
        <div className="multiplayer-header-container">
          <MultiplayerLogo
            className="multiplayer-header-logo"
            onClick={() => {
              triggerPlausibleEvent("HomeFromMenu")
              navigate("/")
            }}
          />
          <div className="multiplayer-header-links">
            <DropdownNavItem
              name="Resources"
              options={[
                {
                  name: "Blog",
                  link: "https://www.multiplayer.app/blog",
                  onClick: () => triggerPlausibleEvent("BlogFromMenu"),
                  icon: <LightBulbIcon />,
                },
                {
                  name: "Learn",
                  link: "/content-library",
                  onClick: () => triggerPlausibleEvent("LearnFromMenu"),
                  icon: <BookIcon />,
                },
                {
                  name: "Join our Discord",
                  link: "https://discord.gg/q9K3mDzfrx",
                  onClick: () => triggerPlausibleEvent("DiscordFromMenu"),
                  icon: <AnnotationIcon />,
                },
              ]}
            />
            <a
              className="nav-item"
              onClick={() => {
                triggerPlausibleEvent("PricingFromMenu")
                navigate("/pricing")
              }}
            >
              Pricing
            </a>
            <a className="nav-item" onClick={() => navigate("/team")}>
              Team
            </a>
            <a
              className="nav-item"
              onClick={() => {
                triggerPlausibleEvent("ContactFromMenu")
                navigate("/contact")
              }}
            >
              Contact
            </a>
            <a
              className="nav-item"
              onClick={() => {
                navigate("/docs")
              }}
            >
              Docs
            </a>
          </div>
          <div className="multiplayer-header-actions">
            <a
              className="nav-item"
              rel="noreferrer"
              target="_blank"
              href="https://go.multiplayer.app"
            >
              Sign In
            </a>
            <a
              className="multiplayer-waitlist-header-control"
              onClick={() => triggerPlausibleEvent("SignupFromMenu")}
              rel="noreferrer"
              target="_blank"
              href="https://cal.com/multiplayer/30min"
            >
              Book a Demo
            </a>
          </div>
        </div>
      </header>
    )
  }

  return <header className="multiplayer-header" />
}

export default Header
